import {
  createWebPlatform,
  type FilePickerProvider,
  type PreparedShare,
  type SaveBytesOptions,
  type SaveOutcome,
  type ShareFile,
  type ShareOutcome,
  type SharePayload,
  type ShareProvider,
  type WebPlatform,
} from '@signbento/platform'
import { AndroidFiles, isAndroidApp } from './android-files.js'
import { isNativeApp } from './native-session.js'

/**
 * The one place the app gets its platform (PLT-01/02). In a browser it is the web platform,
 * unchanged. Inside the installed app the web view cannot download a file and Android's has no
 * Web Share, so saving and sharing go through the native share sheet instead: the bytes are
 * written to the app's own cache and handed to the system, which offers Save to Files, Drive, Mail
 * and the rest. Every other capability keeps its web adapter and its stated fallback.
 */
export function appPlatform(): WebPlatform {
  const web = createWebPlatform()
  if (!isNativeApp()) return web
  const share = createNativeShareProvider()
  return {
    ...web,
    capabilities: { ...web.capabilities, share_url: true, share_files: true },
    providers: {
      ...web.providers,
      share,
      file_picker: createNativeFilePicker(web.providers.file_picker, share),
    },
    disabled_reasons: web.disabled_reasons.filter((reason) => reason.code !== 'SHARE_UNAVAILABLE'),
  }
}

/** Save a text file the person asked for (diagnostics, an account export) wherever the app runs. */
export async function saveTextFile(filename: string, text: string): Promise<SaveOutcome> {
  return appPlatform().providers.file_picker.saveBytes({
    suggested_name: filename,
    media_type: 'application/json',
    bytes: new TextEncoder().encode(text),
  })
}

function createNativeShareProvider(): ShareProvider {
  return {
    canShare: () => true,
    prepare: (payload: SharePayload): PreparedShare => ({ payload, fallback: 'copy_link' }),
    share: async (prepared) => shareNatively(prepared.payload),
  }
}

function createNativeFilePicker(web: FilePickerProvider, share: ShareProvider): FilePickerProvider {
  return {
    // Picking uses the web view's own file input, which both platforms back with the system picker.
    pickFiles: (options) => web.pickFiles(options),
    async saveBytes(options: SaveBytesOptions): Promise<SaveOutcome> {
      if (isAndroidApp()) {
        try {
          const { Filesystem, Directory } = await import('@capacitor/filesystem')
          const name = safeName(options.suggested_name)
          const path = `share/${crypto.randomUUID()}/${name}`
          await Filesystem.writeFile({
            path,
            data: toBase64(options.bytes),
            directory: Directory.Cache,
            recursive: true,
          })
          try {
            const result = await AndroidFiles.saveFile({
              path,
              name,
              mediaType: options.media_type,
            })
            return result.cancelled ? 'cancelled' : 'saved'
          } finally {
            await Filesystem.deleteFile({ path, directory: Directory.Cache }).catch(() => undefined)
          }
        } catch {
          return 'unavailable'
        }
      }
      const file: ShareFile = {
        name: options.suggested_name,
        media_type: options.media_type,
        bytes: options.bytes,
      }
      const outcome = await share.share(share.prepare({ files: [file] }))
      return outcome === 'shared' ? 'saved' : outcome === 'cancelled' ? 'cancelled' : 'unavailable'
    },
  }
}

async function shareNatively(payload: SharePayload): Promise<ShareOutcome> {
  try {
    const [{ Share }, { Filesystem, Directory }] = await Promise.all([
      import('@capacitor/share'),
      import('@capacitor/filesystem'),
    ])
    const files: string[] = []
    for (const file of payload.files ?? []) {
      const written = await Filesystem.writeFile({
        path: `share/${safeName(file.name)}`,
        data: toBase64(file.bytes),
        directory: Directory.Cache,
        recursive: true,
      })
      files.push(written.uri)
    }
    await Share.share({
      ...(payload.title === undefined ? {} : { title: payload.title }),
      ...(payload.text === undefined ? {} : { text: payload.text }),
      ...(payload.url === undefined ? {} : { url: payload.url }),
      ...(files.length === 0 ? {} : { files }),
    })
    return 'shared'
  } catch (error) {
    // The plugin rejects with a cancellation when the person closes the sheet.
    return /cancel/i.test(String((error as { message?: unknown } | null)?.message ?? error))
      ? 'cancelled'
      : 'unavailable'
  }
}

/** A cache file name: no path separators or control characters from a document title. */
function safeName(name: string): string {
  const cleaned = [...name]
    .map((char) => (char.charCodeAt(0) < 0x20 || '\\/:*?"<>|'.includes(char) ? '-' : char))
    .join('')
    .trim()
  return cleaned === '' ? 'signbento-file' : cleaned.slice(0, 120)
}

function toBase64(bytes: Uint8Array): string {
  let binary = ''
  const chunk = 0x8000
  for (let index = 0; index < bytes.length; index += chunk) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunk))
  }
  return btoa(binary)
}
